import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { SITE_BG, SURFACE } from "../../../dark-theme/palette";
import { trackEvent } from "../../../services/GoogleAnalytics";
import Item from "../../components/Item";
import { usePagesTranslation } from "../../locales";

const primaryTitleSx = { fontWeight: 600, color: "#1976d2" };
const sectionTitleSx = { mb: 2, fontWeight: 600, color: "text.primary" };
const bodySx = { fontSize: "1.1rem", lineHeight: 1.8 };

const GRID_SIZE = 12;

/**
 * Cell fills and label colors chosen for strong contrast on small type (WCAG ~AA for typical UI text;
 * aligns with Apple HIG / accessibility expectations in WebView / Safari).
 * Light: near-black on opaque blue-tinted fill (avoids low-contrast blue-on-wash).
 * Dark: white on saturated blue fill (not faint rgba on near-black).
 */
const GRID_PALETTE = {
    light: {
        labeledBg: "#B8CFE8",
        labeledText: "#0A0A0A",
        exampleBg: "rgba(27, 94, 32, 0.14)",
        empty: "#F0F2F5",
        gridLine: "rgba(0, 0, 0, 0.12)",
    },
    dark: {
        labeledBg: "#2A5582",
        labeledText: "#FFFFFF",
        exampleBg: "rgba(102, 187, 106, 0.28)",
        empty: "#1C1C1E",
        gridLine: "rgba(255, 255, 255, 0.14)",
    },
};

/** ~10 labeled cells: four corners, four near ⅓ positions (rows/cols 4 and 8), example endpoints (2,0) & (5,0). */
const LABELED_CELLS = [
    [0, 0],
    [0, 11],
    [11, 0],
    [11, 11],
    [4, 4],
    [4, 8],
    [8, 4],
    [8, 8],
    [2, 0],
    [5, 0],
];

const EXAMPLE_VERTICAL_CELLS = [
    [2, 0],
    [3, 0],
    [4, 0],
    [5, 0],
];

function cellKey(r, c) {
    return `${r},${c}`;
}

function CoordinateGridDiagram({ caption }) {
    const labeled = new Set(LABELED_CELLS.map(([r, c]) => cellKey(r, c)));
    const exampleCol = new Set(EXAMPLE_VERTICAL_CELLS.map(([r, c]) => cellKey(r, c)));

    return (
        <Box
            component="figure"
            sx={{
                m: 0,
                mt: 2,
                mb: 0,
                mx: "auto",
                maxWidth: { xs: "100%", sm: 686 },
            }}
        >
            <Box
                sx={(theme) => {
                    const p = theme.palette.mode === "dark" ? GRID_PALETTE.dark : GRID_PALETTE.light;
                    return {
                        display: "grid",
                        gridTemplateColumns: `repeat(${GRID_SIZE}, minmax(0, 1fr))`,
                        gridTemplateRows: `repeat(${GRID_SIZE}, minmax(0, 1fr))`,
                        gap: "1px",
                        width: "100%",
                        aspectRatio: "1 / 1",
                        bgcolor: p.gridLine,
                        border: 1,
                        borderColor: p.gridLine,
                        borderRadius: 1,
                        overflow: "hidden",
                        boxSizing: "border-box",
                    };
                }}
            >
                {Array.from({ length: GRID_SIZE * GRID_SIZE }, (_, i) => {
                    const r = Math.floor(i / GRID_SIZE);
                    const c = i % GRID_SIZE;
                    const key = cellKey(r, c);
                    const isLabeled = labeled.has(key);
                    const inExample = exampleCol.has(key);

                    return (
                        <Box
                            key={key}
                            sx={{
                                bgcolor: (theme) => {
                                    const p = theme.palette.mode === "dark" ? GRID_PALETTE.dark : GRID_PALETTE.light;
                                    if (isLabeled) return p.labeledBg;
                                    if (inExample) return p.exampleBg;
                                    return p.empty;
                                },
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                textAlign: "center",
                                p: 0.125,
                                minWidth: 0,
                                minHeight: 0,
                                width: "100%",
                                height: "100%",
                                overflow: "hidden",
                                boxSizing: "border-box",
                            }}
                        >
                            {isLabeled ? (
                                <Typography
                                    component="span"
                                    sx={(theme) => {
                                        const p = theme.palette.mode === "dark" ? GRID_PALETTE.dark : GRID_PALETTE.light;
                                        return {
                                            fontSize: { xs: "0.936rem", sm: "1.086rem" },
                                            lineHeight: 1.05,
                                            fontWeight: 800,
                                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                            color: p.labeledText,
                                            WebkitFontSmoothing: "antialiased",
                                            letterSpacing: "0.02em",
                                        };
                                    }}
                                >
                                    {r},{c}
                                </Typography>
                            ) : null}
                        </Box>
                    );
                })}
            </Box>
            {caption ? (
                <Typography
                    component="figcaption"
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1.25, lineHeight: 1.5, textAlign: "center", maxWidth: 811, mx: "auto" }}
                >
                    {caption}
                </Typography>
            ) : null}
        </Box>
    );
}

export default function HowToGridCoordinates() {
    const { t } = usePagesTranslation();

    useEffect(() => {
        trackEvent("entered", { name: "HowToGridCoordinates" });
    }, []);

    return (
        <Box sx={{ backgroundColor: SITE_BG, minHeight: "100vh", width: "100%", overflowX: "hidden" }}>
            <Box sx={{ width: "100%", maxWidth: "100%", margin: 0 }}>
                <Item
                    sx={{
                        p: { xs: 2, md: 4 },
                        textAlign: "left",
                        width: "100%",
                        maxWidth: "100%",
                        boxSizing: "border-box",
                    }}
                >
                    <Typography variant="h3" component="h1" sx={{ mb: 4, ...primaryTitleSx }}>
                        {t("howto.gridCoordinates.page-title")}
                    </Typography>

                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h5" component="h2" sx={sectionTitleSx}>
                            {t("howto.gridCoordinates.grid-title")}
                        </Typography>
                        <Typography variant="body1" sx={{ ...bodySx, mb: 0 }}>
                            {t("howto.gridCoordinates.grid-p")}
                        </Typography>
                        <CoordinateGridDiagram caption={t("howto.gridCoordinates.diagram-caption")} />
                    </Box>

                    <Box sx={{ mb: 5 }}>
                        <Typography variant="h5" component="h2" sx={sectionTitleSx}>
                            {t("howto.gridCoordinates.ai-title")}
                        </Typography>
                        <Typography variant="body1" sx={{ ...bodySx, mb: 2 }}>
                            {t("howto.gridCoordinates.ai-intro")}
                        </Typography>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2.5,
                                mb: 2,
                                borderLeft: 4,
                                borderColor: "primary.main",
                                bgcolor: SURFACE.sunken,
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                {t("howto.gridCoordinates.example-caption")}
                            </Typography>
                            <Typography variant="body1" sx={{ fontStyle: "italic", lineHeight: 1.75 }}>
                                {t("howto.gridCoordinates.example-quote")}
                            </Typography>
                        </Paper>
                        <Typography variant="h6" component="h3" sx={{ mt: 2, mb: 1.5, fontWeight: 600 }}>
                            {t("howto.gridCoordinates.decode-title")}
                        </Typography>
                        <Box component="ul" sx={{ ...bodySx, pl: 2.5, m: 0 }}>
                            <li>
                                <Typography variant="body1" sx={bodySx}>
                                    {t("howto.gridCoordinates.decode-clue")}
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body1" sx={{ ...bodySx, mt: 1.5 }}>
                                    {t("howto.gridCoordinates.decode-vertical")}
                                </Typography>
                            </li>
                            <li>
                                <Typography variant="body1" sx={{ ...bodySx, mt: 1.5 }}>
                                    {t("howto.gridCoordinates.decode-coords")}
                                </Typography>
                            </li>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="h5" component="h2" sx={sectionTitleSx}>
                            {t("howto.gridCoordinates.patterns-title")}
                        </Typography>
                        <Typography variant="body1" sx={{ ...bodySx, mb: 0 }}>
                            {t("howto.gridCoordinates.patterns-p")}
                        </Typography>
                    </Box>
                </Item>
            </Box>
        </Box>
    );
}
